import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { paymentRecoveryService } from '../../../lib/paymentRecoveryService';

const RetryScheduleManager = ({ failedPayments, onRefresh }) => {
  const [retrySchedules, setRetrySchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  useEffect(() => {
    loadRetrySchedules();
  }, [failedPayments]);

  const loadRetrySchedules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('payment_retry_schedule')?.select(`
          *,
          failed_payment:failed_payments!failed_payment_id(
            *,
            user:user_profiles!user_id(id, full_name, email, phone)
          )
        `)?.in('retry_status', ['scheduled', 'in_progress'])?.order('scheduled_date', { ascending: true })?.limit(50);

      setRetrySchedules(data || []);
    } catch (error) {
      console.error('Error loading retry schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteRetry = async (scheduleId) => {
    if (!confirm('Execute this payment retry now?')) return;

    setLoading(true);
    try {
      const { data, error } = await paymentRecoveryService?.executePaymentRetry(scheduleId);

      if (!error) {
        alert(data?.message || 'Retry executed');
        loadRetrySchedules();
        onRefresh?.();
      } else {
        alert('Failed to execute retry');
      }
    } catch (error) {
      alert('Error executing retry');
    } finally {
      setLoading(false);
    }
  };

  const getRetryIntervalLabel = (attempt) => {
    const intervals = {
      1: '24 hours',
      2: '3 days',
      3: '7 days'
    };
    return intervals?.[attempt] || '14 days';
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-800' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
      succeeded: { bg: 'bg-green-100', text: 'text-green-800' },
      failed: { bg: 'bg-red-100', text: 'text-red-800' }
    };
    return colors?.[status] || colors?.scheduled;
  };

  const upcomingRetries = retrySchedules?.filter(s => 
    new Date(s?.scheduled_date) > new Date() && s?.retry_status === 'scheduled'
  );

  const overdueRetries = retrySchedules?.filter(s => 
    new Date(s?.scheduled_date) <= new Date() && s?.retry_status === 'scheduled'
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <Icon name="AlertCircle" className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-600">Overdue Retries</p>
              <p className="text-2xl font-bold text-red-900">{overdueRetries?.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Icon name="Clock" className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-blue-600">Upcoming Retries</p>
              <p className="text-2xl font-bold text-blue-900">{upcomingRetries?.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Icon name="RefreshCw" className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-green-600">Total Scheduled</p>
              <p className="text-2xl font-bold text-green-900">{retrySchedules?.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Retry Schedule List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Retry Schedule</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary mx-auto" />
          </div>
        ) : retrySchedules?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Icon name="CheckCircle" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No retry schedules found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {retrySchedules?.map((schedule) => {
              const statusColor = getStatusColor(schedule?.retry_status);
              const isOverdue = new Date(schedule?.scheduled_date) <= new Date() && schedule?.retry_status === 'scheduled';

              return (
                <div
                  key={schedule?.id}
                  className={`border rounded-lg p-4 ${
                    isOverdue ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">
                          {schedule?.failed_payment?.user?.full_name || 'Unknown Member'}
                        </h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor?.bg} ${statusColor?.text}`}>
                          {schedule?.retry_status?.replace('_', ' ')?.toUpperCase()}
                        </span>
                        {isOverdue && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            OVERDUE
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Amount</p>
                          <p className="font-semibold text-gray-900">${schedule?.amount_attempted}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Attempt</p>
                          <p className="font-semibold text-gray-900">#{schedule?.retry_attempt}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Interval</p>
                          <p className="font-semibold text-gray-900">{getRetryIntervalLabel(schedule?.retry_attempt)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Scheduled Date</p>
                          <p className="font-semibold text-gray-900">
                            {new Date(schedule?.scheduled_date)?.toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Scheduled Time</p>
                          <p className="font-semibold text-gray-900">
                            {new Date(schedule?.scheduled_date)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>

                      {schedule?.result_message && (
                        <div className="mt-2 text-sm text-gray-600">
                          <Icon name="Info" size={14} className="inline mr-1" />
                          {schedule?.result_message}
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      {schedule?.retry_status === 'scheduled' && (
                        <Button
                          size="sm"
                          iconName="Play"
                          onClick={() => handleExecuteRetry(schedule?.id)}
                          disabled={loading}
                        >
                          Execute Now
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Retry Strategy Info */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Icon name="Info" className="w-5 h-5" />
          Intelligent Retry Strategy
        </h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• <strong>Attempt 1:</strong> Retry after 24 hours with same payment method</p>
          <p>• <strong>Attempt 2:</strong> Retry after 3 days with payment method rotation</p>
          <p>• <strong>Attempt 3:</strong> Final retry after 7 days with escalated dunning notification</p>
          <p>• <strong>Optimization:</strong> Retry timing based on member value score and payment history</p>
        </div>
      </div>
    </div>
  );
};

export default RetryScheduleManager;